import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../common/Config";
import Swal from "sweetalert2";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  TablePagination,
  Checkbox,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Chip,
} from "@mui/material";
import { Iconify } from "src/index";
import "../common/commonStyles.css";
import { encryptPayload, resolveEncryptedResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const columns = [
  { id: "checkbox", label: "", numeric: false, width: 40 },
  { id: "code", label: "Code", numeric: false, width: 70 },
  { id: "name", label: "Name", numeric: false, width: 360 },
  { id: "categories", label: "Category", numeric: true, width: 200 },
  { id: "shopifyID", label: "Shopify Status", numeric: true, width: 180 },
];

const ShopifyProduct = () => {
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");

  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryValue, setCategoryValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
    }
  }, [accessToken]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchDebounced(searchValue);
    }, 500);
    return () => clearTimeout(handle);
  }, [searchValue]);

  useEffect(() => {
    fetchCategoriesOptions();
  }, []);

  useEffect(() => {
    if (!accessToken) return;
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    (async () => {
      try {
        const companySettings = await getCompanySettings();
        const useEncrypt = resolveCustomerApiEncrypt(companySettings);
        const catNum =
          categoryValue === "" || categoryValue === null ? 0 : Number(categoryValue);
        const headers = {
          authorization: `Bearer ${token}`,
        };
        let body;
        if (useEncrypt) {
          const requestData = {
            draw: page + 1,
            start: page * rowsPerPage,
            length: rowsPerPage,
            searchValue: searchDebounced,
            searchField: "code",
            categoryValue: catNum,
          };
          const encryptedPayload = encryptPayload(requestData);
          if (!encryptedPayload) {
            if (active) {
              setData([]);
              setTotalRows(0);
              setLoading(false);
            }
            return;
          }
          const formData = new FormData();
          formData.append("encrypted_payload", encryptedPayload);
          body = formData;
        } else {
          const params = new URLSearchParams();
          params.append("draw", String(page + 1));
          params.append("start", String(page * rowsPerPage));
          params.append("length", String(rowsPerPage));
          params.append("searchValue", searchDebounced);
          params.append("searchField", "code");
          params.append("search[value]", searchDebounced);
          params.append("search[regex]", "false");
          params.append("categoryValue", String(catNum));
          params.append("order[0][column]", "0");
          params.append("order[0][dir]", "desc");
          columns.forEach((column, index) => {
            params.append(`columns[${index}][data]`, column.id);
            params.append(`columns[${index}][name]`, "");
            params.append(`columns[${index}][searchable]`, "true");
            params.append(`columns[${index}][orderable]`, "false");
            params.append(`columns[${index}][search][value]`, "");
            params.append(`columns[${index}][search][regex]`, "false");
          });
          body = params.toString();
          headers["Content-Type"] = "application/x-www-form-urlencoded";
        }
        const response = await axios.post(
          `${config.API_URL}/products/getactive_product`,
          body,
          {
            headers,
            signal: controller.signal,
          }
        );
        if (!active) return;
        let json = response.data || {};
        if (useEncrypt) {
          const dec = resolveEncryptedResponse(json);
          if (dec && typeof dec === "object") {
            json = dec;
          }
        }
        const rows = Array.isArray(json.data) ? json.data : [];
        setData(rows);
        const filtered = Number(json.recordsFiltered);
        const total = Number.isFinite(filtered) ? filtered : Number(json.recordsTotal) || rows.length;
        setTotalRows(total);
        if (rows.length === 0 && total > 0 && page > 0 && page * rowsPerPage >= total) {
          setPage(0);
        }
      } catch (err) {
        if (!active || err.name === "CanceledError") return;
        setData([]);
        setTotalRows(0);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [accessToken, page, rowsPerPage, searchDebounced, categoryValue]);

  const fetchCategoriesOptions = async () => {
    try {
      const response = await fetch(`${config.API_URL}/stock_take/getCategories`, {
        method: "POST",
        headers: { authorization: `Bearer ${accessToken}` },
      });
      const result = await response.json();
      const formattedOptions = result.data.category.map((item) => ({
        key: item.name,
        value: item.id,
      }));
      setCategoryOptions(formattedOptions);
    } catch (error) {
      console.error("api fail Error:", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setPage(0);
  };

  const handleCategoryChange = (e) => {
    setCategoryValue(e.target.value);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? data.map((row) => row.id) : []);
  };

  const handleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleClearFilter = () => {
    setSearchValue("");
    setSearchDebounced("");
    setCategoryValue("");
    setPage(0);
  };

  const submitToShopify = async (ids) => {
    Swal.fire({
      title: "Processing...",
      text: "Please wait while we process all products.",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    const formData = new FormData();
    if (ids) {
      ids.forEach((id) => formData.append("val[]", id));
    }

    try {
      const response = await fetch(`${config.API_URL}/shopify/submit-products-to-shopify`, {
        method: "POST",
        headers: { authorization: `Bearer ${accessToken}` },
        body: formData,
      });
      if (!response.ok) throw new Error("Failed to submit selection");
      const result = await response.json();
      Swal.close();
      if (result.success === true) {
        Swal.fire({
          title: "Success!",
          text: ids ? "Products processed successfully." : "Products Created successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        }).then(() => {
          setSelectedIds([]);
          setPage((p) => p); // trigger refetch
        });
      }
    } catch (error) {
      Swal.close();
      Swal.fire({ title: "Error!", text: "Error submitting selection.", icon: "error" });
      console.error("Error submitting selection:", error);
    }
  };

  const handleSelectionSubmit = (e) => {
    e.preventDefault();
    submitToShopify(selectedIds.length > 0 ? selectedIds : null);
  };

  const renderCell = (columnId, row) => {
    if (columnId === "checkbox") {
      return (
        <Checkbox
          size="small"
          className="checkBox"
          checked={selectedIds.includes(row.id)}
          onChange={() => handleSelectRow(row.id)}
          sx={{ padding: "4px", "& .MuiSvgIcon-root": { fontSize: "18px" } }}
        />
      );
    }
    if (columnId === "name") {
      return (
        <Typography
          title={row.name || ""}
          sx={{
            maxWidth: 360,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "0.75rem",
            color: "#212b36",
          }}
        >
          {row.name}
        </Typography>
      );
    }
    if (columnId === "shopifyID") {
      const isAvailable = row.shopifyID !== null;
      return (
        <Chip
          label={isAvailable ? "Available" : "Not Available"}
          color={isAvailable ? "success" : "error"}
          variant="soft"
          size="small"
          sx={{ fontWeight: 500, fontSize: "0.75rem", px: 1.5, height: 24 }}
        />
      );
    }
    return row[columnId] ?? "";
  };

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <form id="picking_create" onSubmit={handleSelectionSubmit}>
            {/* Header Row */}
            <Box sx={{ px: 2, py: 1.2 }}>
              <Grid2 container spacing={2} alignItems="center" justifyContent="space-between">

                {/* Title */}
                <Grid2 size={{ xs: 12, md: 2 }} alignItems="start">
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "#212b36" }}>
                    Shopify Products
                  </Typography>
                </Grid2>

                {/* Search */}
                <Grid2 size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    className="search-textfield"
                    value={searchValue}
                    onChange={handleSearchChange}
                    size="small"
                    placeholder="Search products..."
                    variant="outlined"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: "#637381" }} />
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f5f7fa",
                        "&:hover": { backgroundColor: "#fff" },
                        "&.Mui-focused": { backgroundColor: "#fff" },
                      },
                    }}
                  />
                </Grid2>

                {/* Filters + Actions */}
                <Grid2
                  size={{ xs: 12, md: 7 }}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: { xs: "center", md: "flex-end" },
                    alignItems: { xs: "flex-start", md: "center" },
                    gap: 1.5,
                  }}
                >
                  <FormControl
                    size="small"
                    className="input-border label-border"
                    sx={{ minWidth: 220, width: { xs: "100%", md: "auto" } }}
                  >
                    <InputLabel>Select Categories</InputLabel>
                    <Select
                      value={categoryValue}
                      onChange={handleCategoryChange}
                      label="Select Categories"
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {categoryOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    type="submit"
                    variant="contained"
                    className="button-variant-contained"
                    sx={{ width: { xs: "100%", md: "auto" } }}
                  >
                    Process Product
                  </Button>

                  <Button
                    variant="outlined"
                    color="inherit"
                    className="button-variant-outlined"
                    onClick={() => submitToShopify(null)}
                    sx={{ width: { xs: "100%", md: "auto" } }}
                  >
                    Insert All Products
                  </Button>

                  <Button
                    variant="outlined"
                    color="inherit"
                    className="button-variant-outlined"
                    startIcon={<Iconify icon="eva:refresh-outline" width={18} height={18} />}
                    onClick={handleClearFilter}
                    sx={{ width: { xs: "100%", md: "auto" } }}
                  >
                    Clear
                  </Button>
                </Grid2>
              </Grid2>
            </Box>

            {/* Table */}
            <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
              <Table
                size="small"
                sx={{ tableLayout: "fixed" }}
                className="table-cell-border"
              >
                <colgroup>
                  {columns.map((column) => (
                    <col key={column.id} style={{ width: column.width ? `${column.width}px` : "auto" }} />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell align="center" sx={{ width: "50px" }}>
                      <Checkbox
                        size="small"
                        className="checkBox"
                        checked={data.length > 0 && selectedIds.length === data.length}
                        indeterminate={selectedIds.length > 0 && selectedIds.length < data.length}
                        onChange={handleSelectAll}
                        sx={{ padding: "4px", "& .MuiSvgIcon-root": { fontSize: "18px" } }}
                      />
                    </TableCell>
                    {columns.slice(1).map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.numeric ? "right" : "left"}
                        sx={{ width: column.width ? `${column.width}px` : "auto" }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading
                    ? Array.from({ length: Math.min(rowsPerPage, 10) }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          {columns.map((column) => (
                            <TableCell
                              key={`${column.id}-${index}`}
                              align={column.numeric ? "right" : "left"}
                              sx={{ py: 1, fontSize: "0.75rem", width: column.width ? `${column.width}px` : "auto" }}
                            >
                              <Skeleton variant="text" width="60%" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : data.length > 0
                    ? data.map((row, rowIndex) => (
                        <TableRow
                          key={row.id || rowIndex}
                          hover
                          sx={{
                            "&:hover": { backgroundColor: "rgba(145, 158, 171, 0.04)" },
                            transition: "background-color 0.2s ease",
                          }}
                        >
                          {columns.map((column) => (
                            <TableCell
                              key={column.id}
                              align={
                                column.id === "checkbox"
                                  ? "center"
                                  : column.numeric
                                  ? "right"
                                  : "left"
                              }
                              className="table-cell"
                              sx={{
                                width: column.width ? `${column.width}px` : "auto",
                                ...(column.numeric && { fontWeight: 500 }),
                              }}
                            >
                              {renderCell(column.id, row)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : (
                        <TableRow>
                          <TableCell colSpan={columns.length} sx={{ py: 8 }}>
                            <Typography align="center" sx={{ color: "#637381", fontSize: "0.875rem" }}>
                              No records found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <TablePagination
                component="div"
                count={totalRows}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
                className="pagination-styles"
              />
            </Box>
          </form>
        </Paper>
      </Box>
    </>
  );
};

export default ShopifyProduct;